import emailjs from '@emailjs/browser';
import { compressImageBlob } from './image-compression';
import { COMPANY } from './company-info';

export interface EmailData {
  to: string;
  subject: string;
  message: string;
  attachments?: Blob[];
  attachmentName?: string;
  /**
   * Optionale Bild-Anhänge (z. B. Aufmaß-Fotos), die vor dem Versand
   * automatisch verkleinert werden (max. 1024 px, JPEG ~80 %).
   */
  photoAttachments?: Blob[];
  customerName?: string;
  projectName?: string;
}

/**
 * E-Mail Service für Angebots-Versand
 * 
 * Setup erforderlich:
 * 1. EmailJS Account erstellen: https://www.emailjs.com/
 * 2. Service ID, Template ID und Public Key in .env setzen
 * 3. Template in EmailJS Dashboard konfigurieren
 */
export class EmailService {
  private serviceId: string;
  private templateId: string;
  private publicKey: string;
  private initialized: boolean = false;

  constructor() {
    // Environment Variables für EmailJS
    this.serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID || '';
    this.templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || '';
    this.publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || '';
    
    if (this.serviceId && this.templateId && this.publicKey) {
      this.initialized = true;
      emailjs.init(this.publicKey);
    }
  }

  /**
   * Sendet Angebot per E-Mail
   */
  async sendQuoteEmail(data: EmailData, allowMailtoFallback = true): Promise<boolean> {
    if (!this.initialized) {
      if (!allowMailtoFallback) {
        throw new Error("Der direkte E-Mail-Versand ist nicht eingerichtet. Bitte den Bericht als PDF herunterladen und manuell anhängen.");
      }
      // Fallback: mailto-Link öffnen
      this.openMailtoLink(data);
      return false;
    }

    try {
      // PDF als base64 String für EmailJS
      let attachmentData = '';
      if (data.attachments && data.attachments.length > 0) {
        attachmentData = await this.blobToBase64(data.attachments[0]);
      }

      // Bild-Anhänge vor dem Versand verkleinern (gleiche Komprimierung wie im PDF)
      const compressedPhotos: string[] = [];
      if (data.photoAttachments && data.photoAttachments.length > 0) {
        for (const photo of data.photoAttachments) {
          try {
            const compressed = await compressImageBlob(photo);
            compressedPhotos.push(await this.blobToBase64(compressed));
          } catch (error) {
            console.warn('Foto-Komprimierung für E-Mail fehlgeschlagen:', error);
          }
        }
      }

      const templateParams = {
        to_email: data.to,
        customer_name: data.customerName || 'Sehr geehrte Damen und Herren',
        project_name: data.projectName || 'Ihr Projekt',
        subject: data.subject,
        message: data.message,
        attachment: attachmentData,
        attachment_name: data.attachmentName,
        photo_attachments: compressedPhotos,
        from_name: `${COMPANY.name} – ${COMPANY.legalName}`,
        reply_to: COMPANY.email
      };

      const response = await emailjs.send(
        this.serviceId,
        this.templateId,
        templateParams
      );

      if (response.status === 200) {
        console.log('E-Mail erfolgreich gesendet:', response);
        return true;
      } else {
        throw new Error(`EmailJS Error: ${response.status}`);
      }

    } catch (error) {
      console.error('Fehler beim E-Mail Versand:', error);
      if (!allowMailtoFallback) {
        throw new Error("Der Bericht konnte nicht gesendet werden. Bitte die E-Mail-Konfiguration und Anhangsgröße prüfen und erneut versuchen. Alternativ das PDF herunterladen und manuell versenden.");
      }
      // Fallback: mailto-Link öffnen
      this.openMailtoLink(data);
      return false;
    }
  }

  /** Reports must never claim success or silently lose their PDF through mailto. */
  async sendFacadeReportEmail(data: EmailData): Promise<void> {
    await this.sendQuoteEmail(data, false);
  }

  /**
   * Fallback: Öffnet Standard E-Mail Client mit vorbereitetem Text
   */
  private openMailtoLink(data: EmailData) {
    const subject = encodeURIComponent(data.subject);
    const body = encodeURIComponent(this.generateEmailBody(data));
    const mailtoUrl = `mailto:${data.to}?subject=${subject}&body=${body}`;
    
    window.open(mailtoUrl, '_blank');
  }

  /**
   * Generiert E-Mail Body Text
   */
  private generateEmailBody(data: EmailData): string {
    return `${data.customerName ? `Sehr geehrte/r ${data.customerName}` : 'Sehr geehrte Damen und Herren'},

vielen Dank für Ihr Interesse an unseren Leistungen rund um Fassade, Putz und Sanierung.

${data.projectName ? `Betreff: ${data.projectName}` : ''}

${data.message}

Gerne stehen wir Ihnen für weitere Fragen zur Verfügung.

Mit freundlichen Grüßen
Ihr Team der ${COMPANY.legalName}

---
${COMPANY.legalName} – ${COMPANY.brand}
${COMPANY.address}, ${COMPANY.city}
Tel: ${COMPANY.phone} · Mobil: ${COMPANY.phoneMobile}
E-Mail: ${COMPANY.email}
Web: hofer-seeboden.at`;
  }

  /**
   * Konvertiert Blob zu base64 für E-Mail Anhänge
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Entfernt das "data:application/pdf;base64," Prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Prüft ob E-Mail Service konfiguriert ist
   */
  isConfigured(): boolean {
    return this.initialized;
  }

  /**
   * Sendet Test-E-Mail zur Konfigurationsprüfung
   */
  async sendTestEmail(toEmail: string): Promise<boolean> {
    return this.sendQuoteEmail({
      to: toEmail,
      subject: `${COMPANY.name} – Test E-Mail`,
      message: 'Dies ist eine Test-E-Mail zur Überprüfung der E-Mail Konfiguration.',
      customerName: 'Test Benutzer',
      projectName: 'Test Projekt'
    });
  }
}

/**
 * Vordefinierte E-Mail Templates
 */
export const EmailTemplates = {
  quote: (customerName: string, projectName: string) => ({
    subject: `Angebot: ${projectName}`,
    message: `hiermit übersenden wir Ihnen unser Angebot für das Projekt "${projectName}".

Das Angebot ist 30 Tage gültig und beinhaltet alle beschriebenen Leistungen.

Bei Fragen stehen wir Ihnen gerne zur Verfügung.`
  }),

  reminder: (customerName: string, projectName: string) => ({
    subject: `Erinnerung: Angebot ${projectName}`,
    message: `wir möchten Sie daran erinnern, dass unser Angebot für das Projekt "${projectName}" in Kürze abläuft.

Falls Sie weitere Informationen benötigen oder Fragen haben, melden Sie sich gerne bei uns.`
  }),

  thankYou: (customerName: string, projectName: string) => ({
    subject: `Auftragsbestätigung: ${projectName}`,
    message: `vielen Dank für die Auftragserteilung für das Projekt "${projectName}".

Wir werden uns zeitnah bei Ihnen melden, um die weiteren Schritte zu besprechen.`
  })
};